// frontend/src/pages/print/PrintReceipt.js
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from '../../utils/axios';
import '../../styles/bill.css';

const PrintReceipt = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const format = searchParams.get('format') || 'thermal'; // thermal or a5

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const { data } = await axios.get(`/api/pos/invoices/${id}`);
      
      if (data.success) {
        setInvoice(data.data.invoice);
        // Auto print after data loads
        setTimeout(() => {
          window.print();
        }, 500);
      }
    } catch (error) {
      console.error('Fetch invoice error:', error);
      alert('Баримт татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="print-loading">
        <p>Баримт бэлтгэж байна...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="print-error">
        <p>Баримт олдсонгүй</p>
      </div>
    );
  }

  const vatEnabled = invoice.pos_settings?.pos_enable_vat === '1';
  const vatRate = invoice.pos_settings?.pos_vat_rate || '10';
  
  // Calculate VAT
  const subtotal = invoice.items.reduce((sum, item) => sum + item.subtotal, 0);
  const vatAmount = vatEnabled ? (subtotal * parseFloat(vatRate)) / 100 : 0;
  const totalWithVat = subtotal + vatAmount;
  const finalAmount = totalWithVat - (invoice.discount_amount || 0);

  return (
    <div className={`receipt-wrapper ${format}`}>
      {format === 'thermal' ? (
        /* 80mm Thermal Receipt */
        <div className="receipt thermal-receipt">
          {/* Header */}
          <div className="receipt-header">
            <h1 className="store-name">БОРЛУУЛАЛТЫН БАРИМТ</h1>
            <p className="receipt-number">№: {invoice.invoice_number}</p>
            <p className="receipt-date">
              {new Date(invoice.created_at).toLocaleString('mn-MN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          <div className="divider"></div>

          {/* Customer */}
          {invoice.customer_name && (
            <>
              <div className="customer-info">
                <p><strong>Харилцагч:</strong> {invoice.customer_name}</p>
                {invoice.customer_phone && <p>Утас: {invoice.customer_phone}</p>}
              </div>
              <div className="divider"></div>
            </>
          )}

          {/* Items */}
          <div className="receipt-items">
            <table>
              <thead>
                <tr>
                  <th className="text-left">Бараа</th>
                  <th className="text-center">Тоо</th>
                  <th className="text-right">Үнэ</th>
                  <th className="text-right">Дүн</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td className="item-name">{item.product_name}</td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-right">{item.unit_price.toLocaleString()}</td>
                    <td className="text-right">{item.subtotal.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divider"></div>

          {/* Summary */}
          <div className="receipt-summary">
            <div className="summary-row">
              <span>Дэд дүн:</span>
              <span>{subtotal.toLocaleString()}₮</span>
            </div>
            
            {vatEnabled && (
              <div className="summary-row">
                <span>НӨАТ ({vatRate}%):</span>
                <span>{vatAmount.toLocaleString()}₮</span>
              </div>
            )}

            {invoice.discount_amount > 0 && (
              <div className="summary-row discount">
                <span>Хөнгөлөлт:</span>
                <span>-{invoice.discount_amount.toLocaleString()}₮</span>
              </div>
            )}

            <div className="divider"></div>

            <div className="summary-row total">
              <span>НИЙТ:</span>
              <span>{finalAmount.toLocaleString()}₮</span>
            </div>

            <div className="summary-row">
              <span>Төлсөн:</span>
              <span>{invoice.paid_amount.toLocaleString()}₮</span>
            </div>

            {invoice.change_amount > 0 && (
              <div className="summary-row change">
                <span>Хариу:</span>
                <span>{invoice.change_amount.toLocaleString()}₮</span>
              </div>
            )}

            <div className="summary-row payment-method">
              <span>Төлбөр:</span>
              <span>
                {invoice.payment_method === 'cash' && 'Бэлэн'}
                {invoice.payment_method === 'card' && 'Карт'}
                {invoice.payment_method === 'transfer' && 'Шилжүүлэг'}
                {invoice.payment_method === 'credit' && 'Зээл'}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="divider"></div>
          <div className="receipt-footer">
            <p className="staff-name">Худалдагч: {invoice.staff_name}</p>
            <p className="thank-you">Баярлалаа!</p>
            <p className="contact">Утас: +976 9999-9999</p>
          </div>
        </div>
      ) : (
        /* A5 Receipt */
        <div className="receipt a5-receipt">
          {/* Header */}
          <div className="receipt-header">
            <h1 className="store-name">БОРЛУУЛАЛТЫН БАРИМТ</h1>
            <div className="header-info">
              <div>
                <p className="receipt-number">Баримтын дугаар: <strong>{invoice.invoice_number}</strong></p>
                <p className="receipt-date">
                  Огноо: {new Date(invoice.created_at).toLocaleString('mn-MN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          <hr />

          {/* Customer */}
          {invoice.customer_name && (
            <div className="customer-section">
              <h3>Харилцагчийн мэдээлэл</h3>
              <div className="customer-details">
                <p><strong>Нэр:</strong> {invoice.customer_name}</p>
                {invoice.customer_phone && <p><strong>Утас:</strong> {invoice.customer_phone}</p>}
                {invoice.customer_address && <p><strong>Хаяг:</strong> {invoice.customer_address}</p>}
              </div>
            </div>
          )}

          {/* Items */}
          <div className="items-section">
            <h3>Барааны жагсаалт</h3>
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{width: '40px'}}>№</th>
                  <th>Барааны нэр</th>
                  <th style={{width: '80px'}} className="text-center">Тоо ширхэг</th>
                  <th style={{width: '100px'}} className="text-right">Нэгж үнэ</th>
                  <th style={{width: '100px'}} className="text-right">Дүн</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="item-name">{item.product_name}</div>
                      {item.barcode && <div className="item-code">Баркод: {item.barcode}</div>}
                    </td>
                    <td className="text-center">{item.quantity}</td>
                    <td className="text-right">{item.unit_price.toLocaleString()}₮</td>
                    <td className="text-right">{item.subtotal.toLocaleString()}₮</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="summary-section">
            <div className="summary-grid">
              <div className="summary-left">
                {invoice.notes && (
                  <div className="notes-box">
                    <strong>Тэмдэглэл:</strong>
                    <p>{invoice.notes}</p>
                  </div>
                )}
              </div>
              
              <div className="summary-right">
                <table className="summary-table">
                  <tbody>
                    <tr>
                      <td>Дэд дүн:</td>
                      <td className="text-right">{subtotal.toLocaleString()}₮</td>
                    </tr>
                    
                    {vatEnabled && (
                      <tr>
                        <td>НӨАТ ({vatRate}%):</td>
                        <td className="text-right">{vatAmount.toLocaleString()}₮</td>
                      </tr>
                    )}

                    {invoice.discount_amount > 0 && (
                      <tr className="discount-row">
                        <td>Хөнгөлөлт:</td>
                        <td className="text-right">-{invoice.discount_amount.toLocaleString()}₮</td>
                      </tr>
                    )}

                    <tr className="total-row">
                      <td><strong>НИЙТ ДҮН:</strong></td>
                      <td className="text-right"><strong>{finalAmount.toLocaleString()}₮</strong></td>
                    </tr>

                    <tr>
                      <td>Төлсөн:</td>
                      <td className="text-right">{invoice.paid_amount.toLocaleString()}₮</td>
                    </tr>

                    {invoice.change_amount > 0 && (
                      <tr className="change-row">
                        <td>Хариу:</td>
                        <td className="text-right">{invoice.change_amount.toLocaleString()}₮</td>
                      </tr>
                    )}

                    <tr>
                      <td>Төлбөрийн хэлбэр:</td>
                      <td className="text-right">
                        {invoice.payment_method === 'cash' && 'Бэлэн мөнгө'}
                        {invoice.payment_method === 'card' && 'Карт'}
                        {invoice.payment_method === 'transfer' && 'Шилжүүлэг'}
                        {invoice.payment_method === 'credit' && 'Зээл'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="footer-section">
            <hr />
            <div className="footer-grid">
              <div>
                <p><strong>Худалдагч:</strong> {invoice.staff_name}</p>
              </div>
              <div className="text-right">
                <p><strong>Утас:</strong> +976 9999-9999</p>
                <p>www.example.com</p>
              </div>
            </div>
            <div className="thank-you-message">
              <p>Бидэнтэй хамтран ажилласанд баярлалаа!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintReceipt;